import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const LeadScoring = () => {
  // State variables for Lead Scoring calculation
  const [demographicScore, setDemographicScore] = useState(0); // Score based on customer demographics
  const [behaviorScore, setBehaviorScore] = useState(0); // Score based on customer behavior
  const [engagementScore, setEngagementScore] = useState(0); // Score based on engagement metrics
  const [leadScore, setLeadScore] = useState(0); // Calculated lead score

  // Event handlers to update state values
  const handleDemographicScoreChange = (e) =>
    setDemographicScore(parseFloat(e.target.value) || 0);
  const handleBehaviorScoreChange = (e) =>
    setBehaviorScore(parseFloat(e.target.value) || 0);
  const handleEngagementScoreChange = (e) =>
    setEngagementScore(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate lead score when any input changes
  useEffect(() => {
    const totalScore = demographicScore + behaviorScore + engagementScore;
    setLeadScore(totalScore);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [demographicScore, behaviorScore, engagementScore]);

  return (
    <Layout title="Lead Scoring Calculator" path="lead-scoring">
      <section className="section-padding" id="lead-scoring-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Lead Scoring Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Lead Scoring Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Demographic Score Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="demographicScore" className="form-label">
                        Demographic Score
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Demographic Score:</strong> Represents how well the lead matches your ideal customer profile based on demographics like age, location, and job title."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="demographicScore"
                        placeholder="Enter demographic score"
                        value={demographicScore || ""}
                        onChange={handleDemographicScoreChange}
                      />
                    </div>

                    {/* Behavior Score Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="behaviorScore" className="form-label">
                        Behavior Score
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Behavior Score:</strong> Reflects the actions the lead has taken, such as website visits, downloads, or email opens."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="behaviorScore"
                        placeholder="Enter behavior score"
                        value={behaviorScore || ""}
                        onChange={handleBehaviorScoreChange}
                      />
                    </div>

                    {/* Engagement Score Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="engagementScore" className="form-label">
                        Engagement Score
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Engagement Score:</strong> Measures the level of interest the lead has shown in your business based on actions like responding to emails, engaging with social media, or requesting a demo."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="engagementScore"
                        placeholder="Enter engagement score"
                        value={engagementScore || ""}
                        onChange={handleEngagementScoreChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Lead Score:{" "}
                        <span className="fw-bold ms-3">
                          {leadScore === 0
                            ? "0"
                            : leadScore.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          out of 100
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Lead Scoring Calculator
                </h3>

                {/* What is a Lead Scoring Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Lead Scoring Calculator?
                </h4>
                <p>
                  A **Lead Scoring Calculator** is a tool used to evaluate the
                  potential of a lead to convert into a customer by assigning
                  scores based on factors like demographics, behavior, and
                  engagement.
                </p>

                {/* Why Use a Lead Scoring Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Lead Scoring Calculator?
                </h4>
                <p>
                  This calculator helps sales and marketing teams prioritize
                  their efforts by identifying high-potential leads, improving
                  efficiency in outreach, and increasing conversion rates.
                </p>

                {/* Benefits of Using a Lead Scoring Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Lead Scoring Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Prioritizes High-Potential Leads:</strong> Ensures
                    sales teams focus on leads most likely to convert.
                  </li>
                  <li>
                    <strong>Increases Marketing Efficiency:</strong> Helps
                    tailor campaigns to engage the right audience.
                  </li>
                  <li>
                    <strong>Supports Customer Acquisition Strategies:</strong>{" "}
                    Provides data-driven insights to enhance sales and marketing
                    tactics.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Lead Scoring Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Sales Teams:</strong> To focus efforts on promising
                    leads.
                  </li>
                  <li>
                    <strong>Marketing Teams:</strong> To design targeted
                    campaigns based on lead scores.
                  </li>
                  <li>
                    <strong>B2B Services:</strong> To identify and nurture leads
                    in the pipeline effectively.
                  </li>
                  <li>
                    <strong>SaaS Companies:</strong> To improve subscription and
                    conversion rates.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  What factors influence lead scoring?
                </h4>
                <p>
                  Key factors include demographic fit (e.g., job title,
                  industry), behavioral signals (e.g., website visits,
                  downloads), and engagement (e.g., email open rates, event
                  attendance).
                </p>

                <h4 className="mt-4 mb-3">
                  How can lead scoring improve sales efficiency?
                </h4>
                <p>
                  By focusing on leads with higher scores, sales teams can
                  allocate resources effectively and close deals faster, leading
                  to higher revenue and improved conversion rates.
                </p>

                <h4 className="mt-4 mb-3">Can lead scoring be automated?</h4>
                <p>
                  Yes, lead scoring can be automated using CRM systems and
                  marketing automation tools that assign scores based on
                  predefined criteria and real-time interactions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LeadScoring;
